import { useEffect, useRef, type RefObject } from 'react';
import { pickInitialFocusTarget } from './focusTrap';

/**
 * MVP-11 #28: `Dialog.tsx` and `Sheet.tsx` each carried an independent ~80-line copy of this
 * effect (Sheet.tsx's own docblock called the duplication out as a deliberate, temporary
 * trade-off — "a far smaller risk than refactoring a file that many other builders depend on").
 * Wave 2 lands the one implementation both now share. Same contract either one had: claim focus
 * into the panel two animation frames after open (letting `Portal`'s own child insertion and any
 * dev-mode StrictMode mount/cleanup/mount replay settle first — see the P1 fix round 5 incident
 * this was built to fix), trap `Tab` at the panel's edges, `Escape` closes, and focus returns to
 * the invoking element on close (DESIGN.md §6.1: "Dialogs trap focus, `Esc` closes, focus returns
 * to the invoking element").
 */
export function useFocusTrap(panelRef: RefObject<HTMLElement | null>, open: boolean, onClose: () => void): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Read the latest `onClose` through a ref instead of listing it as an effect dependency — a
  // caller that passes an inline `onClose` (a fresh function identity every render) must not
  // re-run this effect on every keystroke inside the panel (see Dialog.tsx/Sheet.tsx's own P1 fix
  // history: each re-run's cleanup moved focus back out, then its setup yanked focus onto
  // `focusable[0]`, so typing worked for one character before focus jumped to Close).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = panel?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // A child that requests its own `autoFocus` (e.g. `SingerPicker`'s name field, guest
    // `RequestSheet`'s `SingerNameField`) wins over `focusable[0]` (the header's Close button,
    // first in DOM order) — the actual decision is `focusTrap.ts`'s pure, unit-tested function.
    function claimFocus() {
      if (!panel || panel.contains(document.activeElement)) return;
      // Both `.dialog-scrim` and `.sheet-scrim` are `position: fixed`, so the panel is always
      // fully within the viewport regardless of document scroll — `preventScroll` makes that
      // explicit rather than relying on browser default behaviour agreeing.
      (pickInitialFocusTarget(panel, focusable) ?? panel).focus({ preventScroll: true });
    }

    // Claiming focus synchronously here used to race two things that regularly won: `Portal`'s
    // own child insertion (an `autoFocus` element's one-time `.focus()` runs in the same commit,
    // not necessarily before this effect) and, in dev, StrictMode's mount→cleanup→mount replay of
    // this very effect. Two `requestAnimationFrame`s let the commit and any replay finish first,
    // then claim, then re-claim once more only if something still knocked focus out to `<body>`.
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      claimFocus();
      secondFrame = requestAnimationFrame(() => {
        if (document.activeElement === document.body) claimFocus();
      });
    });

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      document.removeEventListener('keydown', onKeyDown, true);
      // Restoring focus to the invoking element (e.g. a `SongTile` inside library.css's
      // virtualised, internally-scrolling results list) with a plain `.focus()` lets the browser's
      // default scroll-into-view kick in for that element's scrollable ancestors — including
      // `document`/`body`, which `base.css` gives `overflow: hidden` but does not stop from being
      // scrolled programmatically. `preventScroll` keeps this call to exactly what it is meant to
      // do: return keyboard focus, without the side effect.
      previouslyFocused.current?.focus({ preventScroll: true });
    };
    // `onClose` deliberately excluded — read via `onCloseRef` above. Only `open` toggling should
    // run the focus-capture/restore cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
